import { Typography, TextSize } from '@shared/index';
import { formatNum } from '@app/utils/math';
import { TableThead, TableTr, TableTh, Table, TableBody, TableTd } from '@widgets/table';
import { useMemo, useState } from 'react';
import { getCurrentDate } from '@shared/utils/getCurrentDate';
import clsx from 'clsx';
import { getQuarterCurrent } from '@app/utils/quarters';
import { useGetTeamQuarterlyBonusResults } from '@entities/managers-perfomance';
import { CalendarDays } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { QUARTERS_FOR_SELECT } from '@app/contstants/months';

const CIRCLE_COLORS = [
  '#84D9FA',
  '#317896',
  '#017BAB',
  '#005171',
  '#072E46',
];

const getManagerColor = (index: number) => {
  return CIRCLE_COLORS[Math.min(index, CIRCLE_COLORS.length - 1)];
};

export const QuarterlyResult = () => {
  const { year: currentYear } = getCurrentDate();
  const currentQuarter = getQuarterCurrent();
  
  const [selectedQuarter, setSelectedQuarter] = useState<number>(currentQuarter);
  const [isOpen, setIsOpen] = useState(false);

  const { data: teamQuarterlyBonusResults = [] } = useGetTeamQuarterlyBonusResults({
    year: currentYear,
    quarter: selectedQuarter,
  });

  const { managersData, totalHashrate } = useMemo(() => {
    if (!teamQuarterlyBonusResults.length) {
      return { managersData: [], totalHashrate: 0 };
    }

    const mapped = teamQuarterlyBonusResults.map(result => ({
      id: result.manager_id,
      name: result.manager_name,
      result: result.hashrate,
    }));

    const total = mapped.reduce((sum, manager) => sum + manager.result, 0);

    return {
      managersData: mapped,
      totalHashrate: total,
    };
  }, [teamQuarterlyBonusResults]);

  const selectedQuarterLabel = QUARTERS_FOR_SELECT.find(
    quarters => Number(quarters.value) === selectedQuarter
  )?.label || '';

  const handleQuarterSelect = (quarterValue: string) => {
    setSelectedQuarter(Number(quarterValue));
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <Typography size={TextSize.size24} className="font-semibold" title="Показатели менеджеров за квартал" />
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <div className="w-[138px] flex items-center bg-[#202020] rounded-md p-[10px] cursor-pointer hover:bg-[#2a2a2a] transition-colors">
              <Typography size={TextSize.size14} className="text-gray-400" text={selectedQuarterLabel} />
              <CalendarDays size={16} className="ml-auto" />
            </div>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="bg-[#202020] text-white rounded-md overflow-hidden z-50 py-2 outline-none border border-[#2a2a2a]"
              align="end"
              sideOffset={8}
              alignOffset={0}
              style={{ width: 'var(--radix-popover-trigger-width)' }}
            >
              <div className="max-h-[300px] overflow-y-auto">
                {QUARTERS_FOR_SELECT.map(quarter => {
                  const isSelected = Number(quarter.value) === selectedQuarter;
                  return (
                    <div
                      key={quarter.value}
                      className={clsx(
                        'px-4 py-2 cursor-pointer hover:bg-[#2a2a2a] transition-colors',
                        isSelected && 'bg-[#2a2a2a] text-white'
                      )}
                      onClick={() => handleQuarterSelect(quarter.value)}
                    >
                      <Typography
                        size={TextSize.size14}
                        className={isSelected ? 'text-white font-medium' : 'text-gray-400'}
                        text={quarter.label}
                      />
                    </div>
                  );
                })}
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
      <div className='h-[380px] overflow-y-auto'>
        <Table>
          <TableThead className="text-white/40 [&_div]:justify-start sticky top-0 z-10">
            <TableTr withoutUnderline>
              <TableTh title="Место" className="w-[5%]" />
              <TableTh title="Имя/Фамилия" className="w-[50%]" />
              <TableTh title="Хешрейт (Ph)" className="w-[20%]" />
               <TableTh
                title={
                  <div className="flex flex-wrap">
                    <span>% от общего</span>
                    <span>заведенного хешрейта</span>
                  </div>
                } 
                className="w-[25%]" 
              />
            </TableTr>
          </TableThead>
          <TableBody>
            {managersData.map((item, index) => {
              const isOdd = index % 2 === 0;
              const percentage = totalHashrate > 0 ? Math.round((item.result / totalHashrate) * 100) : 0;
              const place = index + 1;
              const circleColor = getManagerColor(index);

              return (
                <TableTr
                  key={item.id}
                  style={{ backgroundColor: isOdd ? '#1D1D1D' : 'unset' }}
                  withoutUnderline
                >
                  <TableTd
                    className={clsx(isOdd && 'rounded-l-[16px]')}
                    title={
                      <Typography
                        size={TextSize.size14}
                        text={place.toString()}
                      />
                    }
                  />
                  <TableTd
                    title={
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-[10px] h-[10px] rounded-full" 
                          style={{ backgroundColor: circleColor }}
                        />
                        <Typography
                          size={TextSize.size14}
                          text={item.name}
                        />
                      </div>
                    }
                  />
                  <TableTd
                    title={
                      <Typography
                        size={TextSize.size14}
                        text={formatNum(item.result)}
                      />
                    }
                  />
                  <TableTd
                    title={
                      <Typography
                        size={TextSize.size14}
                        text={`${percentage}%`}
                      />
                    }
                    className={clsx(isOdd && 'rounded-r-[16px]')}
                  />
                </TableTr>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};